"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import StoryCard from "../story/StoryCard";
import SwiperButton from "../buttons/SwiperButton";
import { Swiper as SwiperType } from "swiper";
import { useState, useCallback, useEffect } from "react";
import { useUpdateStories } from "@/app/lib/features/story/story.hook";
import { HomeStory } from "@/app/lib/features/home/home.types";

interface StoriesSwiperProps {
  stories: HomeStory[];
}

const StoriesSwiper = ({ stories }: StoriesSwiperProps) => {
  const updateStories = useUpdateStories();
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  useEffect(() => {
    if (stories.length > 0) updateStories(stories);
  }, [stories, updateStories]);

  const handleSwiper = useCallback((swiper: SwiperType) => {
    requestAnimationFrame(() => {
      swiper.update();
      setShowLeft(!swiper.isBeginning);
      setShowRight(!swiper.isEnd);
    });
  }, []);

  // slideChange + resize aynı işi yapıyor. resize ŞART: slidesPerView "auto"
  // olduğu için taşma olup olmadığı kapsayıcı genişliğine bağlı — pencere
  // daralınca isEnd değişiyor ama slideChange tetiklenmiyordu, gradientler
  // eski hâlinde kalıyordu.
  const handleEdgeChange = useCallback((swiper: SwiperType) => {
    setShowLeft(!swiper.isBeginning);
    setShowRight(!swiper.isEnd);
  }, []);

  if (stories.length === 0) return <></>;

  // stories-row: slide sayısı satıra sığıyorsa kartları eşit aralıkla yayan
  // saf CSS kuralının tutamağı — bkz. globals.css `.stories-row`.
  return (
    <div className="stories-row container-gutter-left relative overflow-visible">
      <Swiper
        modules={[FreeMode]}
        spaceBetween={10}
        slidesPerView={4.8}
        className="stories-swiper relative mt-6"
        observer={false}
        observeParents={false}
        onSwiper={handleSwiper}
        onSlideChange={handleEdgeChange}
        onResize={handleEdgeChange}
        watchSlidesProgress={false}
        freeMode={{ enabled: true, momentumRatio: 0.25, momentumVelocityRatio: 0.25 }}
        /* Her yerde SAYISAL slidesPerView: slide genişliği
             (kapsayıcı − boşluklar) / slidesPerView
           ile hesaplanıyor, yani satır her zaman kapsayıcıyı tam dolduruyor.
           Kart (daire) sabit boyutta ve slide'ın ortasında durduğu için
           daireler arası mesafe = (slide − daire) + spaceBetween.

           BUNUN SONUCU: aralığı `spaceBetween` ile daraltmak İŞE YARAMAZ —
           boşluk küçülünce slide aynı kadar genişler, mesafe değişmez. Tek
           gerçek kaldıraç slidesPerView.

           ≥1440px'te 13 SÜTUN: canlıdaki 13 story container-main'i tam
           dolduruyor (slide 86.8px, daire 76px — sığıyor). Daha dar
           masaüstlerinde bilerek daha az sütun var; 13 story oraya sığmadığı
           için satır taşıyor ve swiper butonları + kenar gradientleri
           kendiliğinden devreye giriyor.

           slidesPerView'a dokunulursa globals.css'teki İKİ blok da
           güncellenmeli: `.stories-row` yayma eşikleri ve
           `.stories-swiper:not(.swiper-initialized)` ilk-kare genişlikleri. */
        breakpoints={{
          480: { slidesPerView: 5.8, spaceBetween: 10 },
          640: { slidesPerView: 7.0, spaceBetween: 12 },
          /* 768-1023 tablet: freeMode AÇIK kalıyor — satır burada hâlâ
             full-bleed ve dokunmatik, serbest kaydırma mobil hissini koruyor.
             Serbest kaydırma yalnızca butonlu masaüstünde (≥1024) kapanıyor. */
          768: { slidesPerView: 8.0, spaceBetween: 12 },
          1024: { slidesPerView: 9.5, spaceBetween: 14, freeMode: { enabled: false } },
          1280: { slidesPerView: 11.0, spaceBetween: 14, freeMode: { enabled: false } },
          1440: { slidesPerView: 13.0, spaceBetween: 14, freeMode: { enabled: false } },
        }}
      >
        {stories.map((story, i) => (
          <SwiperSlide key={story.id}>
            <StoryCard story={story} index={i} />
          </SwiperSlide>
        ))}

        {/* Butonlar <Swiper>'ın İÇİNDE olmak ZORUNDA: SwiperButton instance'ı
            useSwiper() ile context'ten alıyor ve bulamazsa null render ediyor.
            Wrapper'a taşındıklarında context dışında kaldıkları için kap boş
            basılıyordu — DOM'da <div class="...md:flex"></div> görünüp hiç
            buton çıkmamasının sebebi buydu. Gradientler wrapper'da kalabilir,
            onlar context'e değil state'e bağlı. */}
        <div className="absolute z-50 top-1/2 -translate-y-1/2 left-0 w-full items-center justify-between px-3 pointer-events-none hidden lg:flex">
          <SwiperButton position="left" slideMode="group" />
          <SwiperButton position="right" slideMode="group" />
        </div>
      </Swiper>
      {/* Fade'ler Swiper'ın değil wrapper'ın çocuğu: Swiper'ın kendi kutusunda
          left-0 taşan slide'ların üstünde beyaz şerit bırakırdı. Wrapper'da
          left-0/right-0 satırın gerçek kenarı = container-main kenarı.
          Butonlarla birlikte yalnızca lg+ basılıyorlar: <1024px'te satır
          full-bleed ve dokunmatik, tek gezinme yolu parmakla kaydırmak. */}
      <div
        className={`absolute top-0 left-0 z-20 w-13.75 h-full bg-linear-to-r from-white to-transparent pointer-events-none hidden lg:block transition-opacity duration-300 ${
          showLeft ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        className={`absolute top-0 right-0 z-20 w-13.75 h-full bg-linear-to-l from-white to-transparent pointer-events-none hidden lg:block transition-opacity duration-300 ${
          showRight ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
};

export default StoriesSwiper;